import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, ExternalLink, GitMerge } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import AppUnavailable from "@/pages/AppUnavailable";

export default function KBDuplicates() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [threshold, setThreshold] = useState(0.72);
  const [keepBy, setKeepBy] = useState({});   // group key -> article_id to keep
  const [busy, setBusy] = useState("");

  const load = useCallback((thr) => {
    setData(null);
    api.get("/kb/duplicates", { params: { threshold: thr } })
      .then((r) => {
        setData(r.data);
        const defaults = {};
        r.data.groups.forEach((g) => { defaults[g.key] = g.articles[0].article_id; });
        setKeepBy(defaults);
      })
      .catch((e) => { if (e.response?.status === 403) setDenied(true); else setData({ groups: [], count: 0 }); });
  }, []);
  useEffect(() => { load(threshold); }, [load, threshold]);

  const merge = async (g) => {
    const keep = keepBy[g.key];
    const drop = g.articles.map((a) => a.article_id).filter((id) => id !== keep);
    if (!drop.length) { toast.error("Nothing to merge"); return; }
    if (!window.confirm(`Keep "${g.articles.find((a) => a.article_id === keep)?.name}" and delete ${drop.length} other(s)? Related links will be re-pointed.`)) return;
    setBusy(g.key);
    try {
      const { data: res } = await api.post("/kb/merge", { keep, drop });
      toast.success(`Kept "${res.kept}", removed ${res.removed}`);
      setData((d) => ({ ...d, groups: d.groups.filter((x) => x.key !== g.key), count: d.count - 1 }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (denied) return <AppUnavailable appName="KB Duplicates" inactive={false} />;

  return (
    <div className="kb-surface" data-testid="kb-duplicates-page">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/kb")} className="text-slate-400 dark:text-slate-500 hover:text-primary" data-testid="kb-dup-back"><ArrowLeft className="h-5 w-5" /></button>
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-slate-100">Find duplicate articles</h1>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span>Match strictness</span>
          <input type="range" min="0.55" max="0.95" step="0.01" value={threshold} data-testid="kb-dup-threshold"
            onChange={(e) => setThreshold(Number(e.target.value))} className="w-40" />
          <span className="w-10 font-mono text-xs">{Math.round(threshold * 100)}%</span>
        </div>
      </div>

      {!data ? (
        <div className="flex h-72 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>
      ) : data.groups.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-800 py-16 text-center text-sm text-slate-400 dark:text-slate-500" data-testid="kb-dup-empty">
          No near-duplicate titles found at this strictness. Try lowering it.
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">{data.count} group{data.count === 1 ? "" : "s"} of similar articles. Pick which to keep, then merge — related links from the others are re-pointed before deletion.</p>
          {data.groups.map((g) => (
            <div key={g.key} data-testid={`kb-dup-group-${g.key}`} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {g.articles.map((a) => (
                  <label key={a.article_id} data-testid={`kb-dup-option-${a.article_id}`}
                    className="flex cursor-pointer items-center gap-3 py-2.5">
                    <input type="radio" name={`keep-${g.key}`} checked={keepBy[g.key] === a.article_id}
                      onChange={() => setKeepBy((k) => ({ ...k, [g.key]: a.article_id }))}
                      className="h-4 w-4 accent-[hsl(var(--primary))]" data-testid={`kb-dup-keep-${a.article_id}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{a.name}</span>
                        {a.similarity < 1 && <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 text-[10px] text-slate-500 dark:text-slate-400">{Math.round(a.similarity * 100)}% match</span>}
                        {a.source === "intercom" && <span className="rounded bg-violet-100 px-1.5 text-[10px] text-violet-700">Intercom</span>}
                        <span className={`rounded px-1.5 text-[10px] ${a.scope === "external" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"}`}>{a.scope === "external" ? "All" : "Staff"}</span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
                        {a.category_name || "—"} · {a.word_count} words · {a.hj_views} views · updated {a.source_updated_at || "—"}
                      </div>
                    </div>
                    <a href={`/kb/a/${a.slug}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                      className="shrink-0 text-slate-300 dark:text-slate-600 hover:text-primary" data-testid={`kb-dup-open-${a.article_id}`}><ExternalLink className="h-4 w-4" /></a>
                  </label>
                ))}
              </div>
              <div className="mt-3 flex justify-end">
                <Button onClick={() => merge(g)} disabled={busy === g.key} data-testid={`kb-dup-merge-${g.key}`}
                  className="bg-primary text-primary-foreground hover:bg-primary/90">
                  {busy === g.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitMerge className="h-4 w-4" />}
                  Merge &amp; delete {g.articles.length - 1} other{g.articles.length - 1 === 1 ? "" : "s"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
